import { useRef, useState } from 'react'
import type { OutputFormat } from '../../types/transcription'
import { apiPostForm } from '../../api/client'
import { extractAudioSample } from '../../lib/audioSample'
import { isVideoFile } from '../../lib/videoTypes'
import {
  MIN_DETECT_CONFIDENCE,
  matchesDetected,
  decideAutoLanguage,
  sttLanguageForIso,
  type DetectedLanguage,
} from '../../lib/languageDetect'
import { STT_LANGUAGES } from '../../lib/languages'
import { useT } from '../../i18n/useT'
import { trackLanguageAutoDetected, trackLanguageMismatch, trackVideoProcessed } from '../../analytics/posthog'
import { LanguageSelect } from './LanguageSelect'
import { FormatSelect } from './FormatSelect'
import { FileUpload, type UploadProcessing } from './FileUpload'
import { MicRecorder } from './MicRecorder'
import { ConvertButton } from './ConvertButton'
import { LanguageMismatchModal } from './LanguageMismatchModal'
import { LanguageUnsureModal } from './LanguageUnsureModal'
import { afterPaint } from '../../lib/afterPaint'
import { MAX_DURATION_SECONDS, exceedsMaxDuration } from '../../lib/audioChunks'
import { mediaDurationSeconds } from '../../lib/mediaDuration'

interface TranscriptionFormProps {
  isLoading: boolean
  credits: number
  monthlyCredits?: boolean
  format: OutputFormat
  onFormatChange: (format: OutputFormat) => void
  /** `picked` is the file the person chose; `file` is what goes on the wire.
   * They differ only for a video, whose audio track was extracted — and then
   * they differ in both name and size. */
  onSubmit: (file: File, language: string, picked: PickedFile) => void
}

/** What the reader believes they are transcribing */
export interface PickedFile {
  name: string
  size: number
}

interface DetectApiResponse {
  language: string
  name: string
  confidence: number
}

interface Mismatch {
  detected: DetectedLanguage
  suggested: { name: string; code: string }
}

/** Detection could not honestly name a language we transcribe.
 *  `unsupported` — it is sure, and sure of something we do not offer.
 *  `unsure` — the guess is one of ours, but barely. */
interface Unsure {
  kind: 'unsupported' | 'unsure'
  detected: DetectedLanguage
  /** The STT language to go ahead with, when there is one worth offering */
  fallback: { name: string; code: string } | null
}

interface DetectionEntry {
  file: File
  promise: Promise<DetectedLanguage | null>
  result: DetectedLanguage | null
  done: boolean
}

// How long a submit waits for an in-flight detection before giving up
const DETECT_WAIT_MS = 15_000

/** Best-effort: decode a sample in the browser and ask the backend which
 * language it hears. Video extraction already produces a ready sample —
 * `presample` skips the WebAudio decode in that case. */
async function detectLanguage(file: File, presample: Blob | null = null): Promise<DetectedLanguage | null> {
  try {
    const sample = presample ?? (await extractAudioSample(file))
    if (!sample) return null
    const formData = new FormData()
    formData.append('file', sample, 'sample.wav')
    const resp = await apiPostForm<DetectApiResponse>('/detect-language', formData)
    if (!resp.language) return null
    return { iso: resp.language, name: resp.name || resp.language, confidence: resp.confidence }
  } catch {
    return null
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ])
}

export function TranscriptionForm({ isLoading, credits, monthlyCredits, format, onFormatChange, onSubmit }: TranscriptionFormProps) {
  const t = useT()
  const [language, setLanguage] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [languageError, setLanguageError] = useState(false)
  const [mismatch, setMismatch] = useState<Mismatch | null>(null)
  const [unsure, setUnsure] = useState<Unsure | null>(null)
  // True while a submit is waiting for an in-flight detection to finish
  const [isResolving, setIsResolving] = useState(false)
  // Set while a picked video is having its audio track extracted
  const [processing, setProcessing] = useState<UploadProcessing | null>(null)
  /** What to show — the video's name and size, when the upload is its
   * extracted track */
  const [picked, setPicked] = useState<PickedFile | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const detectionRef = useRef<DetectionEntry | null>(null)
  // File whose mismatch warning the user already answered with "keep"
  const mismatchAckRef = useRef<File | null>(null)
  // Bumped on every new pick so a stale extraction can't clobber a newer one
  const extractRunRef = useRef(0)

  function acceptFile(f: File | null, presample: Blob | null, shownAs?: PickedFile) {
    setFile(f)
    setPicked(f ? (shownAs ?? { name: f.name, size: f.size }) : null)
    setLanguageError(false)
    mismatchAckRef.current = null
    if (!f) {
      detectionRef.current = null
      return
    }
    // Detection decodes audio and encodes a WAV sample — start it only after
    // the picked file has been painted, not inside the change event
    const entry: DetectionEntry = {
      file: f,
      promise: afterPaint().then(() => detectLanguage(f, presample)),
      result: null,
      done: false,
    }
    detectionRef.current = entry
    void entry.promise.then((result) => {
      entry.result = result
      entry.done = true
    })

    // Say it at the moment of picking rather than at the moment of pressing
    // Start. Not awaited: reading the metadata takes a beat, and holding the
    // file off the screen for it would be a worse trade than showing it and
    // taking it back. `transcribe` refuses the same file anyway, so nothing
    // rides on winning this race.
    void mediaDurationSeconds(f).then((seconds) => {
      if (detectionRef.current !== entry) return // the file was swapped
      if (!exceedsMaxDuration(seconds)) return
      setFileError(t('err.tooLong', { hours: MAX_DURATION_SECONDS / 3600 }))
      setFile(null)
      setPicked(null)
      detectionRef.current = null
    })
  }

  function handleFileChange(f: File | null) {
    extractRunRef.current++
    setFileError(null)
    setProcessing(null)
    if (f && isVideoFile(f.name)) {
      void handleVideoFile(f)
      return
    }
    acceptFile(f, null)
  }

  /** Videos never upload as-is: the audio track is extracted in the browser
   * (lazy-loaded ffmpeg.wasm) and the result rejoins the normal audio flow. */
  async function handleVideoFile(f: File) {
    const runId = extractRunRef.current
    setFile(null)
    detectionRef.current = null
    setProcessing({ label: t('form.preparingConverter'), ratio: null })
    try {
      const { extractAudioFromVideo } = await import('../../lib/videoAudio')
      const result = await extractAudioFromVideo(f, (p) => {
        if (extractRunRef.current !== runId) return
        if (p.phase === 'encode') {
          setProcessing({ label: t('form.convertingTrack'), ratio: p.ratio })
        } else if (p.phase === 'copy') {
          setProcessing({ label: t('form.extractingTrack'), ratio: null })
        }
      })
      if (extractRunRef.current !== runId) return
      setProcessing(null)
      trackVideoProcessed(f.name.split('.').pop()?.toLowerCase() || '', f.size)
      acceptFile(result.audio, result.sample, result.original)
    } catch (e) {
      if (extractRunRef.current !== runId) return
      setProcessing(null)
      setFileError(e instanceof Error && e.message ? e.message : t('err.videoExtract'))
    }
  }

  function findMismatch(selectedLang: string, detected: DetectedLanguage | null): Mismatch | null {
    if (!detected) return null
    if (detected.confidence < MIN_DETECT_CONFIDENCE) return null
    if (matchesDetected(selectedLang, detected.iso)) return null
    const suggested = sttLanguageForIso(detected.iso)
    if (!suggested || suggested.code === selectedLang) return null
    return { detected, suggested }
  }

  /** The audio is confidently a language we do not transcribe. Nothing used
   * to be said: `findMismatch` returns null when there is no STT language to
   * suggest, so the file went through and came back as whatever the gateway
   * made of it. */
  function findUnsupported(selectedLang: string, detected: DetectedLanguage | null): Unsure | null {
    if (!detected) return null
    if (detected.confidence < MIN_DETECT_CONFIDENCE) return null
    if (matchesDetected(selectedLang, detected.iso)) return null
    if (sttLanguageForIso(detected.iso)) return null
    return { kind: 'unsupported', detected, fallback: null }
  }

  async function handleSubmit() {
    if (!file || isResolving) return
    setLanguageError(false)

    // Both paths need the detection result, so a submit that races an
    // in-flight detection waits for it — otherwise an explicitly chosen
    // language would silently skip the mismatch warning
    const entry = detectionRef.current
    let detected: DetectedLanguage | null = null
    if (entry && entry.file === file) {
      if (entry.done) {
        detected = entry.result
      } else {
        setIsResolving(true)
        try {
          detected = await withTimeout(entry.promise, DETECT_WAIT_MS)
        } finally {
          setIsResolving(false)
        }
        // The user may have swapped the file while we were waiting
        if (detectionRef.current !== entry) return
      }
    }

    // Explicit selection → warn when the audio clearly sounds like another language
    if (language) {
      if (mismatchAckRef.current !== file) {
        const found = findMismatch(language, detected)
        if (found) {
          setMismatch(found)
          return
        }
        const foreign = findUnsupported(language, detected)
        if (foreign) {
          setUnsure(foreign)
          return
        }
      }
      onSubmit(file, language, picked ?? { name: file.name, size: file.size })
      return
    }

    // No language selected → the detection decides, but only when it can
    // actually name something. It used to be acted on whatever the
    // confidence, so a file in a language the model has never heard was
    // transcribed as its nearest guess, silently.
    const decision = decideAutoLanguage(detected)
    if (decision.kind === 'none') {
      setLanguageError(true)
      return
    }
    if (mismatchAckRef.current !== file) {
      if (decision.kind === 'unsupported') {
        setUnsure({ kind: 'unsupported', detected: detected!, fallback: null })
        return
      }
      if (decision.kind === 'unsure') {
        setUnsure({ kind: 'unsure', detected: detected!, fallback: decision.language })
        return
      }
    }
    if (decision.kind === 'unsupported') {
      setLanguageError(true)
      return
    }

    const auto = decision.language
    trackLanguageAutoDetected(auto.code)
    setLanguage(auto.code)
    onSubmit(file, auto.code, picked ?? { name: file.name, size: file.size })
  }

  function selectedLanguageName(): string {
    const known = STT_LANGUAGES.find((l) => l.code === language)
    return known ? t(`langName.${known.code}`) : language
  }

  function handleUseDetected() {
    if (!file || !mismatch) return
    trackLanguageMismatch({
      detectedName: mismatch.suggested.name,
      detectedCode: mismatch.detected.iso,
      selectedSttCode: language,
      action: 'switched',
    })
    const code = mismatch.suggested.code
    setLanguage(code)
    setMismatch(null)
    onSubmit(file, code, picked ?? { name: file.name, size: file.size })
  }

  function handleKeepSelected() {
    if (!file || !mismatch) return
    trackLanguageMismatch({
      detectedName: mismatch.suggested.name,
      detectedCode: mismatch.detected.iso,
      selectedSttCode: language,
      action: 'kept',
    })
    mismatchAckRef.current = file
    setMismatch(null)
    onSubmit(file, language, picked ?? { name: file.name, size: file.size })
  }

  /** What "go ahead anyway" would mean here, or nothing when it would mean
   * transcribing in a language nobody chose. */
  function unsureContinueLabel(): string | undefined {
    if (!unsure) return undefined
    if (unsure.fallback) return t('unsure.continue', { name: t(`langName.${unsure.fallback.code}`) })
    // Detection found nothing we offer; only an explicit pick can go ahead
    if (language) return t('unsure.continue', { name: selectedLanguageName() })
    return undefined
  }

  function handleUnsureChoose() {
    setUnsure(null)
    // Send them to the picker rather than leaving the form looking untouched
    setLanguageError(true)
  }

  function handleUnsureContinue() {
    if (!file || !unsure) return
    const code = unsure.fallback?.code ?? language
    if (!code) return
    mismatchAckRef.current = file
    setUnsure(null)
    setLanguage(code)
    onSubmit(file, code, picked ?? { name: file.name, size: file.size })
  }

  function handleMismatchClose() {
    if (mismatch) {
      trackLanguageMismatch({
        detectedName: mismatch.suggested.name,
        detectedCode: mismatch.detected.iso,
        selectedSttCode: language,
        action: 'dismissed',
      })
    }
    setMismatch(null)
  }

  const canSubmit = !!file && !isLoading && !isResolving && !processing

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LanguageSelect
          value={language}
          onChange={(v) => { setLanguage(v); setLanguageError(false) }}
          error={languageError}
        />
        <FormatSelect value={format} onChange={onFormatChange} />
      </div>

      <MicRecorder disabled={isLoading || !!processing} onRecorded={handleFileChange} />

      <FileUpload
        file={file}
        picked={picked ?? undefined}
        onFileChange={handleFileChange}
        credits={credits}
        monthlyCredits={monthlyCredits}
        error={fileError}
        processing={processing}
        busy={isLoading}
      />

      {languageError && (
        <p className="text-xs text-error -mt-3">{t('err.cantDetect')}</p>
      )}

      <ConvertButton
        isLoading={isLoading || isResolving}
        disabled={!canSubmit}
        onClick={handleSubmit}
      />

      <LanguageMismatchModal
        isOpen={!!mismatch}
        detectedName={
          mismatch?.suggested ? t(`langName.${mismatch.suggested.code}`) : mismatch?.detected.name || ''
        }
        confidence={mismatch?.detected.confidence || 0}
        selectedName={selectedLanguageName()}
        onUseDetected={handleUseDetected}
        onKeepSelected={handleKeepSelected}
        onClose={handleMismatchClose}
      />

      <LanguageUnsureModal
        isOpen={!!unsure}
        kind={unsure?.kind ?? 'unsure'}
        // A language we transcribe has a name in the catalog; one we don't
        // only has whatever the detector called it, in English
        detectedName={
          unsure?.fallback ? t(`langName.${unsure.fallback.code}`) : unsure?.detected.name || ''
        }
        confidence={unsure?.detected.confidence ?? 0}
        onChoose={handleUnsureChoose}
        continueLabel={unsureContinueLabel()}
        onContinue={unsureContinueLabel() ? handleUnsureContinue : undefined}
        onClose={handleUnsureChoose}
      />
    </div>
  )
}
